import { CalendarCheck, Languages, MapPin, MessageSquare, Navigation, Sparkles } from "lucide-react";
import { Link } from "react-router-dom";
import PresenceDot from "../PresenceDot";
import { useChat } from "../../contexts/ChatContext";
import { useAuth } from "../../contexts/AuthContext";

const norm = (s) => (s || "").trim().toLowerCase();

function fmtNextSlot(iso) {
  if (!iso) return null;
  try {
    const d = new Date(iso);
    const now = new Date();
    const diffDays = Math.floor((d - now) / (1000 * 60 * 60 * 24));
    const timeLabel = d.toLocaleTimeString(undefined, { hour: "2-digit", minute: "2-digit" });
    if (diffDays <= 0) return `Today · ${timeLabel}`;
    if (diffDays === 1) return `Tomorrow · ${timeLabel}`;
    if (diffDays < 7) return `${d.toLocaleDateString(undefined, { weekday: "short" })} · ${timeLabel}`;
    return d.toLocaleDateString(undefined, { day: "numeric", month: "short" });
  } catch { return null; }
}

export default function CounsellorStep({ counsellors, selected, onSelect, clientCity, geoStatus }) {
  const { getCounsellorPresence } = useChat() || {};
  const { user } = useAuth() || {};
  if (counsellors.length === 0) {
    return (
      <div className="col-span-2 brand-card p-10 text-center text-[#7A7D96]">
        No counsellors available yet. Please check back soon.
      </div>
    );
  }

  // Client's location signals — profile is authoritative; fall back to browser-geolocated
  // city when a client hasn't filled in their profile yet. `norm` lower-cases + trims so
  // comparisons are case/whitespace-insensitive.
  const myCity = norm(user?.city || clientCity);
  const mySuburb = norm(user?.suburb);
  const myProvince = norm(user?.province);

  // Sort order (best-match first):
  //   1) Counsellors with at least one open slot (bookable now) come before those with none.
  //   2) Within each availability bucket, sort by location proximity:
  //        same suburb (0) < same city (1) < same province (2) < any other (3).
  //   3) Stable within a proximity bucket so backend ordering is preserved.
  const proximityRank = (c) => {
    if (mySuburb && norm(c.suburb) === mySuburb) return 0;
    if (myCity && norm(c.city) === myCity) return 1;
    if (myProvince && norm(c.province) === myProvince) return 2;
    return 3;
  };
  const sorted = [...counsellors].sort((a, b) => {
    const aHasSlots = (a.total_open_slots || 0) > 0 ? 0 : 1;
    const bHasSlots = (b.total_open_slots || 0) > 0 ? 0 : 1;
    if (aHasSlots !== bHasSlots) return aHasSlots - bHasSlots;
    return proximityRank(a) - proximityRank(b);
  });

  // Human-readable label for the top-of-list banner so clients understand WHY the order
  // they see is personalised (open-slots first, then closest to them).
  const geoBanner = (() => {
    const openCount = sorted.filter((c) => (c.total_open_slots || 0) > 0).length;
    const nearCount = sorted.filter((c) => proximityRank(c) <= 2).length;
    const locationLabel = user?.suburb || user?.city || user?.province || clientCity;
    if (locationLabel) {
      return {
        tone: "match",
        text: `Showing ${openCount} counsellor${openCount === 1 ? "" : "s"} with open bookings first, then closest to you (${locationLabel})${nearCount > 0 ? ` — ${nearCount} nearby` : ""}.`,
      };
    }
    if (geoStatus === "denied") {
      return { tone: "info", text: "Location sharing off — showing counsellors with open bookings first. Add your city in your profile to see nearby ones prioritised." };
    }
    if (geoStatus === "asking") {
      return { tone: "info", text: "Finding counsellors near you — allow location for a personalised order." };
    }
    return { tone: "info", text: `Showing ${openCount} counsellor${openCount === 1 ? "" : "s"} with open bookings first.` };
  })();

  return (
    <div className="space-y-4">
      {geoBanner && (
        <div
          className={`flex items-start gap-3 p-3 rounded-2xl border text-sm ${
            geoBanner.tone === "match" ? "bg-secondary/10 border-secondary/30 text-[#3D405B]" : "bg-muted/40 border-[#3D405B]/10 text-[#5F627D]"
          }`}
          data-testid="book-geo-banner"
        >
          <Navigation size={16} className="text-secondary shrink-0 mt-0.5" />
          <span>{geoBanner.text}</span>
        </div>
      )}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-5" data-testid="book-step-1">
        {sorted.map((c) => {
          const isSelected = selected?.user_id === c.user_id;
          const presence = getCounsellorPresence ? getCounsellorPresence(c.user_id) : (c.online ? "available" : "away");
          const reachable = presence !== "away";
          const rank = proximityRank(c);
          const hasSlots = (c.total_open_slots || 0) > 0;
          const nearBadge = rank === 0
            ? { label: "Same suburb", testid: `counsellor-near-you-${c.user_id}` }
            : rank === 1
              ? { label: "Same city", testid: `counsellor-near-you-${c.user_id}` }
              : rank === 2
                ? { label: "Same province", testid: `counsellor-near-you-${c.user_id}` }
                : null;
          const activate = () => onSelect(c);
          const handleKey = (e) => {
            if (e.key === "Enter" || e.key === " ") { e.preventDefault(); activate(); }
          };
          return (
            <div
              key={c.user_id}
              role="button"
              tabIndex={0}
              onClick={activate}
              onKeyDown={handleKey}
              className={`text-left brand-card p-6 w-full transition-all cursor-pointer focus:outline-none focus:ring-2 focus:ring-primary/40 ${isSelected ? "ring-2 ring-primary shadow-md" : "brand-card-hover"} ${nearBadge ? "border-secondary/40" : ""}`}
              data-testid={`counsellor-card-${c.user_id}`}
              data-has-slots={hasSlots ? "true" : "false"}
              data-proximity-rank={String(rank)}
            >
              <div className="flex items-start gap-4">
                {c.picture ? (
                  <img src={c.picture} alt={c.name} className="w-16 h-16 rounded-2xl object-cover" />
                ) : (
                  <div className="w-16 h-16 rounded-2xl bg-accent flex items-center justify-center text-2xl font-extrabold text-[#3D405B]">{c.name?.[0]}</div>
                )}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <div className="font-extrabold text-[#3D405B] text-lg">{c.name}</div>
                    <PresenceDot userId={c.user_id} />
                    {nearBadge && (
                      <span className="inline-flex items-center gap-1 pill bg-secondary/20 text-secondary" data-testid={nearBadge.testid}>
                        <Sparkles size={10} /> {nearBadge.label}
                      </span>
                    )}
                    {c.next_slot_at ? (
                      <span
                        className="inline-flex items-center gap-1 pill bg-primary/10 text-primary border border-primary/25"
                        data-testid={`counsellor-next-slot-${c.user_id}`}
                        title={`First open slot: ${new Date(c.next_slot_at).toLocaleString()}`}
                      >
                        <CalendarCheck size={10} /> Next: {fmtNextSlot(c.next_slot_at)}
                      </span>
                    ) : (
                      <span
                        className="inline-flex items-center gap-1 pill bg-muted text-[#7A7D96] border border-[#3D405B]/10"
                        data-testid={`counsellor-no-slots-${c.user_id}`}
                        title="This counsellor hasn't published any open availability yet"
                      >
                        <CalendarCheck size={10} /> No open slots yet
                      </span>
                    )}
                  </div>
                  {(c.city || c.office_address) && (
                    <div className="mt-1 flex items-start gap-1.5 text-xs text-[#5F627D]" data-testid={`counsellor-location-${c.user_id}`}>
                      <MapPin size={12} className="shrink-0 mt-0.5 text-primary/70" />
                      <span className="min-w-0">
                        {c.city ? <span className="font-bold">{c.city}</span> : null}
                        {c.city && c.office_address ? " · " : ""}
                        {c.office_address ? <span>{c.office_address}</span> : null}
                      </span>
                    </div>
                  )}
                  <div className="text-xs text-[#7A7D96] uppercase tracking-[0.18em] font-bold mb-2 mt-2">Registered counsellor</div>
                  <div className="flex flex-wrap gap-1.5">
                    {(c.specialties || []).slice(0, 4).map((s) => (
                      <span key={s} className="pill bg-secondary/15 text-secondary">{s}</span>
                    ))}
                  </div>
                </div>
              </div>
              <p className="text-sm text-[#5F627D] leading-relaxed mt-4">{c.bio}</p>
              <div className="mt-3 flex flex-wrap items-center gap-3 justify-between">
                {c.languages?.length > 0 ? (
                  <div className="flex items-center gap-1.5 text-xs text-[#7A7D96]"><Languages size={13} /> {c.languages.join(" · ")}</div>
                ) : <span />}
                {reachable && (
                  <Link
                    to={`/chat?with=${c.user_id}`}
                    onClick={(e) => e.stopPropagation()}
                    onKeyDown={(e) => e.stopPropagation()}
                    className="inline-flex items-center gap-1.5 px-3.5 py-1.5 rounded-full text-xs font-extrabold text-white bg-secondary hover:-translate-y-0.5 shadow-sm transition-all"
                    data-testid={`counsellor-quickchat-${c.user_id}`}
                    aria-label={`Message ${c.name}`}
                  >
                    <MessageSquare size={12} /> Message {c.name?.split(" ")[0]}
                  </Link>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
